import type { CalculatorConfig, FieldValues, ValidationContext } from '../../engine/types';
import { calculate, type HsaSavingsResult } from './calculate';
import { HsaSavingsResultsView } from './ResultsView';
import { PlanTypeField } from './PlanTypeField';
import { HsaSavingsSpotlightResultsView } from './SpotlightResultsView';
import { HsaSavingsOdonlowResultsView } from './OdonlowResultsView';

export interface HsaSavingsValues extends FieldValues {
  planType: 'individual' | 'family';
  contributionAmt: number | null;
  medicalExpenses: number | null;
  years: number | null;
  fedTaxPct: number | null;
  stateTaxPct: number | null;
  interestRatePct: number | null;
}

/** Mirrors hsa-savings-calc.js's contributionLimit resolution (base + catch-up). */
function contributionLimitFor(ctx: ValidationContext): number {
  const hsa = ctx.limits.current.hsa;
  return ctx.values.planType === 'family'
    ? hsa.maxContributionFamily + hsa.catchupFamily
    : hsa.maxContributionSingle + hsa.catchupSingle;
}

function maxOutOfPocketFor(ctx: ValidationContext): number {
  const hsa = ctx.limits.current.hsa;
  return ctx.values.planType === 'family' ? hsa.maxOutOfPocketFamily : hsa.maxOutOfPocketSingle;
}

// The federal-bracket stepper snaps through these values instead of
// incrementing — the 2018 IRS marginal brackets, same as the legacy calc.
const FEDERAL_TAX_BRACKETS = [10, 12, 22, 24, 32, 35, 37];

export const hsaSavingsConfig: CalculatorConfig<HsaSavingsValues, HsaSavingsResult> = {
  id: 'hsa-savings',
  title: 'HSA Savings Calculator',
  resultsPlacement: 'always',
  recompute: 'live',
  progress: 'none',
  // Prod's screen-reader text for every stepper, including the years and %
  // fields (Brad, 2026-10-01: match prod).
  stepperLabels: { increase: 'Increase Dollar Amount', decrease: 'Decrease Dollar Amount' },
  // Out-of-range typed values stay in the field with prod's error message
  // showing, and results keep the last valid value (content team,
  // 2026-10-08: match prod; previously clamped silently on blur). All layouts.
  invalidInput: 'hold',
  // Error limits read "$5500" with no comma, as on prod (2026-10-09; the
  // "$5,500" form approved Oct 1 is reverted pending legal/compliance).
  initialValues: {
    planType: 'individual',
    contributionAmt: 2000,
    medicalExpenses: 250,
    years: 10,
    fedTaxPct: 24,
    stateTaxPct: 5,
    interestRatePct: 3,
  },
  steps: [
    {
      id: 'inputs',
      items: [
        {
          kind: 'custom',
          id: 'planType',
          render: ({ values, setValue }) => <PlanTypeField values={values} setValue={setValue} />,
        },
        {
          kind: 'field',
          type: 'currency',
          name: 'contributionAmt',
          label: 'Average contribution per year:',
          srLabel: 'Average contribution per year:',
          tooltip:
            "Enter the amount of money you'll put into your HSA for the year. This includes contributions from your employer. Visit hsabank.com/irs-guidelines for current maximum contribution amounts. Depending on your plan type (single or family), what you enter shouldn't exceed the maximum amount.",
          stepper: true,
          step: 25,
          min: 0,
          max: contributionLimitFor,
          validate: (value, _v, ctx) => {
            const max = contributionLimitFor(ctx);
            const n = value as number | null;
            if (n === null || n < 0 || n > max) {
              return `Please enter a dollar amount between 0 and $${max}.`;
            }
            return null;
          },
        },
        {
          kind: 'field',
          type: 'currency',
          name: 'medicalExpenses',
          label: 'Average medical expenses per year:',
          srLabel: 'Average medical expenses per year:',
          tooltip: 'Enter the estimated amount of money you spend out-of-pocket on medical care.',
          stepper: true,
          step: 25,
          min: 0,
          max: maxOutOfPocketFor,
          validate: (value, _v, ctx) => {
            const max = maxOutOfPocketFor(ctx);
            const n = value as number | null;
            if (n === null || n < 0 || n > max) {
              return `Please enter a dollar amount between 0 and $${max}.`;
            }
            return null;
          },
        },
        {
          kind: 'field',
          type: 'number',
          name: 'years',
          // Prod's colon after the "?" (2026-10-09: was dropped Oct 1, reverted).
          label: 'How many years will you have your HSA?:',
          srLabel: 'How many years will you have your HSA?:',
          stepper: true,
          step: 1,
          min: 1,
          max: 30,
          validate: (value) => {
            const n = value as number | null;
            if (n === null || n <= 0 || n > 30) {
              return 'Please enter a number of years between 1 and 30.';
            }
            return null;
          },
        },
        {
          kind: 'field',
          type: 'percent',
          name: 'fedTaxPct',
          label: 'Federal income tax bracket:',
          srLabel: 'Federal income tax bracket:',
          tooltip:
            'There are seven marginal income tax brackets for each federal filing status: 10, 12, 22, 24, 32, 35, and 37 percent.',
          stepper: true,
          snapTo: FEDERAL_TAX_BRACKETS,
          min: 10,
          max: 37,
          validate: (value) => {
            const n = value as number | null;
            if (n === null || n < 10 || n > 37) {
              return 'Please enter a percentage between 10 and 37.';
            }
            return null;
          },
        },
        {
          kind: 'field',
          type: 'percent',
          name: 'stateTaxPct',
          label: 'State income tax bracket:',
          srLabel: 'State income tax bracket:',
          tooltip: "Consult your state's individual tax rules and regulations to find out this percentage.",
          stepper: true,
          step: 1,
          min: 0,
          max: 12,
          validate: (value) => {
            const n = value as number | null;
            if (n === null || n < 0 || n > 12) {
              return 'Please enter a percentage less than or equal to 12.';
            }
            return null;
          },
        },
        {
          kind: 'field',
          type: 'percent',
          name: 'interestRatePct',
          label: 'Rate of return:',
          srLabel: 'Rate of return:',
          tooltip: "Review your HSA administrator's current rates.",
          stepper: true,
          step: 0.1,
          min: 0,
          max: 20,
          validate: (value) => {
            const n = value as number | null;
            if (n === null || n < 0 || n > 20) {
              // Prod's wording, though 0 is accepted (Brad, 2026-10-01: match prod).
              return 'Please enter a percentage between 0.1 and 20.';
            }
            return null;
          },
        },
      ],
    },
  ],
  calculate,
  ResultsView: HsaSavingsResultsView,
};

/**
 * Year values the Spotlight stepper walks through: every year to 15, then
 * 5-year jumps. The Design B notes said "above 10", but from the default 10
 * an up-click landing on 15 instead of 11 was jarring (Brad, 2026-09-28).
 * Typing any value 1-30 still works.
 */
const SPOTLIGHT_YEAR_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 20, 25, 30];

/**
 * "Spotlight" layout (Brad, 2026-09-28; the content team's Design B), picked
 * per placement via hsaSavingsCalc.yaml's Layout field. Same fields, math and
 * validation as the legacy layout; the plan-type radios move into the results
 * view (above the chart), the years stepper snaps per SPOTLIGHT_YEAR_STEPS,
 * and steppers use trailing circled arrows beside a boxless value (Brad,
 * 2026-10-01: like the bottom of the Design B mockup).
 */
export const hsaSavingsSpotlightConfig: CalculatorConfig<HsaSavingsValues, HsaSavingsResult> = {
  ...hsaSavingsConfig,
  layout: 'spotlight',
  resultsOrder: 'before',
  stepperLayout: 'arrows',
  inputStyle: 'inline',
  steps: hsaSavingsConfig.steps.map((step) => ({
    ...step,
    items: step.items
      .filter((item) => !(item.kind === 'custom' && item.id === 'planType'))
      .map((item) =>
        item.kind === 'field' && item.name === 'years' && item.type === 'number'
          ? { ...item, snapTo: SPOTLIGHT_YEAR_STEPS }
          : item,
      ),
  })),
  ResultsView: HsaSavingsSpotlightResultsView,
};

/**
 * "Odonlow" layout (Brad, 2026-10-01; lead designer's mockup). Same fields,
 * wording, values, steps and validation as the legacy layout (years step by 1,
 * as on prod); the plan type moves into the results view, results come first,
 * and the steppers are chevrons stacked inside each field. Rounded unless the
 * calculator's Style is Square.
 */
export const hsaSavingsOdonlowConfig: CalculatorConfig<HsaSavingsValues, HsaSavingsResult> = {
  ...hsaSavingsConfig,
  layout: 'odonlow',
  resultsOrder: 'before',
  stepperLayout: 'stacked',
  inputStyle: 'fit',
  steps: hsaSavingsConfig.steps.map((step) => ({
    ...step,
    items: step.items.filter((item) => !(item.kind === 'custom' && item.id === 'planType')),
  })),
  ResultsView: HsaSavingsOdonlowResultsView,
};
